/**
 * ImportVulnerabilitiesModal Component
 *
 * Modal for importing vulnerabilities from various security scanning tools.
 * Uses the generic ImportModal component with vulnerability-specific import sources.
 * Includes validation and preview capabilities for supported file formats.
 *
 * @module sections/vulnerabilities/components/modals
 */

import React from 'react';
import { toast } from 'sonner';

import { ImportAction, ImportModal } from '@/components/import';
import { VULNERABILITY_IMPORT_ACTIONS } from '@/components/import/constants/importActions';
import { useAxios } from '@/hooks/useAxios';
import { useUploadFile } from '@/hooks/useFiles';

export interface ImportVulnerabilitiesModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Optional callback when import is confirmed with action and file */
  onImport?: (action: ImportAction, file: File) => void;
}

/**
 * ImportVulnerabilitiesModal Component
 *
 * Displays a multi-stage modal for importing vulnerabilities from various security tools.
 * Includes file upload, validation, and preview stages for supported formats.
 *
 * @param props - Component props
 * @returns Import modal JSX
 *
 * @example
 * ```tsx
 * <ImportVulnerabilitiesModal
 *   open={showImportModal}
 *   onClose={() => setShowImportModal(false)}
 *   onImport={(action, file) => {
 *     console.log('Import from:', action.value);
 *     console.log('File:', file.name);
 *     // Process the uploaded file based on the selected action
 *   }}
 * />
 * ```
 */
export function ImportVulnerabilitiesModal({
  open,
  onClose,
  onImport,
}: ImportVulnerabilitiesModalProps) {
  const axios = useAxios();
  const { mutateAsync: uploadFile } = useUploadFile();

  /**
   * Handle file selection - uploads to S3 immediately
   * Returns the S3 file path for later use
   */
  const handleFileSelected = async (
    action: ImportAction,
    file: File
  ): Promise<string> => {
    // Step 1: Construct the file path
    const filePath = `imports/${action.value}/${file.name}`;

    // Step 2: Convert file to ArrayBuffer for upload
    const arrayBuffer = await file.arrayBuffer();

    // Step 3: Upload file to S3 via presigned URL
    const uploadPromise = uploadFile({
      name: filePath,
      content: arrayBuffer,
      ignoreSnackbar: true, // We'll handle toasts manually
    });

    await toast.promise(uploadPromise, {
      loading: `Uploading ${file.name}...`,
      success: `${file.name} uploaded successfully`,
      error: `Failed to upload ${file.name}`,
    });

    return filePath;
  };

  /**
   * Handle import confirmation - triggers backend processing
   * File is already uploaded to S3 at this point
   */
  const handleImport = async (
    action: ImportAction,
    file: File,
    filePath: string
  ) => {
    try {
      // Trigger import via POST /account/<action>-import
      const importPromise = axios.post(`/account/${action.value}-import`, {
        secret: {},
        value: filePath,
      });

      await toast.promise(importPromise, {
        loading: `Processing ${action.label} import...`,
        success: `Import from ${action.label} completed successfully`,
        error: `Failed to process ${action.label} import`,
      });

      // Call optional callback
      if (onImport) {
        onImport(action, file);
      }

      // Close modal on success
      onClose();
    } catch (error) {
      console.error('Import failed:', error);
      // Toast errors are already shown by toast.promise
    }
  };

  return (
    <ImportModal
      open={open}
      onClose={onClose}
      title="Import Vulnerabilities"
      actions={VULNERABILITY_IMPORT_ACTIONS}
      onFileSelected={handleFileSelected}
      onImport={handleImport}
    />
  );
}
